/**
 * Overlay screen-reader announcer (HOS-1832).
 *
 * The embed overlay hosts a single visually-hidden `aria-live="polite"` region
 * (rendered by `GlassOverlay`). Flow steps push short, meaningful messages to it
 * — availability loaded, step changes, and payment/booking errors — via the
 * `useAnnounce()` hook.
 *
 * The context default is a no-op, so the SPA (which never mounts `GlassOverlay`
 * and therefore never provides a real announcer) is completely unaffected: every
 * `announce(...)` call in the shared flow pages compiles to a no-op there.
 *
 * Debounce/dedupe (so a re-rendering flow can't spam the region) lives in the
 * provider (`GlassOverlay`), NOT here — this module is only the wiring.
 */
import { createContext, useContext } from 'react';

/** Push a short message to the overlay's polite live region. */
export type Announce = (message: string) => void;

const noop: Announce = () => {};

export const AnnouncerContext = createContext<Announce>(noop);

/** Returns the overlay announcer, or a no-op outside the embed overlay. */
export const useAnnounce = (): Announce => useContext(AnnouncerContext);
